import {
  extractDeliverySource,
  extractWarningOrderDeliverySource,
  isWarningOrderSource,
} from "./boundaries";
import {
  cleanDeliveryBody,
  stripMarkdownStrongEmphasis,
  toSpokenText,
} from "./clean";
import { splitIntoChunks } from "./chunks";
import {
  normalizeHeading,
  paragraphIdentity,
  subparagraphIdentity,
} from "./headings";
import type {
  BoundaryResult,
  ChunkNode,
  ItemNode,
  ItemType,
  OrdersParseTree,
  ParagraphNode,
  ParsedCue,
  ParserWarning,
  ParseResult,
  SubparagraphNode,
} from "./types";

type BoundaryExtractor = (source: string) => BoundaryResult;

interface MutableParagraph {
  displayHeading: string;
  id: string;
  items: ItemNode[];
  kind: "PARA";
  sourceLine: number;
  spokenHeading: string;
  subparagraphs: MutableSubparagraph[];
}

interface MutableSubparagraph {
  displayHeading: string;
  id: string;
  items: ItemNode[];
  kind: "SUBPARA";
  sourceLine: number;
  spokenHeading: string;
}

interface PendingItem {
  displayText: string;
  label?: string;
  sourceLine: number;
  spokenText: string;
  type: ItemType;
}

const PARA_HEADING = /^##\s+(.+?)\s*$/;
const SUBPARA_HEADING = /^###\s+(.+?)\s*$/;
const LABEL_ITEM = /^\*\*(.+?)\s*(?::\s*\*\*|\*\*\s*:)\s*(.*)$/;
const NUMBERED_ITEM = /^\d+[.)]\s+(.+)$/;
const BULLET_ITEM = /^[-*]\s+(.+)$/;
const MALFORMED_TIME = /(?<!\d)\d{6}\s+hrs\b/i;

function itemText(label: string, value: string): string {
  return `${label}: ${value}`.trimEnd();
}

function tableCells(line: string): string[] | null {
  if (!/^\|.*\|$/.test(line)) return null;
  return line
    .slice(1, -1)
    .split("|")
    .map((cell) => cell.trim());
}

function isTableDelimiter(cells: readonly string[]): boolean {
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function appendContinuation(
  pending: PendingItem,
  display: string,
  spoken: string,
): void {
  pending.displayText = `${pending.displayText} ${display}`;
  pending.spokenText = `${pending.spokenText} ${spoken}`;
}

function buildItem(
  pending: PendingItem,
  containerId: string,
  itemIndex: number,
): ItemNode {
  const id = `${containerId}/item-${String(itemIndex + 1).padStart(2, "0")}`;
  const displayChunks = splitIntoChunks(pending.displayText);
  const chunks: ChunkNode[] = displayChunks.map((displayText, index) => ({
    id: `${id}/chunk-${String(index + 1).padStart(2, "0")}`,
    kind: "CHUNK",
    displayText,
    spokenText: toSpokenText(displayText),
  }));
  const base = {
    id,
    kind: "ITEM" as const,
    type: pending.type,
    displayText: pending.displayText,
    spokenText: pending.spokenText,
    sourceLine: pending.sourceLine,
    chunks,
  };

  return pending.label === undefined ? base : { ...base, label: pending.label };
}

function warningForHeading(heading: string, line: number): ParserWarning {
  return {
    code: "UNRECOGNIZED_HEADING",
    heading,
    line,
    message: `Unrecognized heading retained as written: ${heading}`,
  };
}

function parseMarkdown(
  source: string,
  extractBoundary: BoundaryExtractor,
): ParseResult {
  const boundary = extractBoundary(source);
  if (!boundary.ok) return { ok: false, error: boundary };

  const malformedTimeIndex = boundary.body
    .split("\n")
    .findIndex((line) => MALFORMED_TIME.test(line));
  if (malformedTimeIndex >= 0) {
    const line = boundary.startLine + malformedTimeIndex;
    return {
      ok: false,
      error: {
        code: "MALFORMED_TIME",
        line,
        message: `Malformed time at line ${String(line)}: use four clock digits and keep date elements separate.`,
      },
    };
  }

  const cleaned = cleanDeliveryBody(boundary.body);
  const initialDisplayHeading = normalizeHeading(
    stripMarkdownStrongEmphasis(
      boundary.body
        .split("\n", 1)
        .join("")
        .replace(/^##\s+/, ""),
    ),
  );
  const initialIdentity = paragraphIdentity(initialDisplayHeading);
  let currentParagraph: MutableParagraph = {
    id: initialIdentity.id,
    kind: "PARA",
    displayHeading: initialDisplayHeading,
    spokenHeading: initialIdentity.spoken,
    sourceLine: boundary.startLine,
    items: [],
    subparagraphs: [],
  };
  const paragraphs: MutableParagraph[] = [currentParagraph];
  const warnings: ParserWarning[] = initialIdentity.recognized
    ? []
    : [warningForHeading(initialDisplayHeading, boundary.startLine)];
  let currentSubparagraph: MutableSubparagraph | undefined;
  let pending: PendingItem | undefined;

  const flushPending = (): void => {
    if (pending === undefined) return;
    const container = currentSubparagraph ?? currentParagraph;
    container.items.push(
      buildItem(pending, container.id, container.items.length),
    );
    pending = undefined;
  };

  for (const [index, displayLine] of cleaned.displayLines.entries()) {
    if (index === 0) continue;
    const plainDisplayLine = stripMarkdownStrongEmphasis(displayLine);
    const spokenLine = toSpokenText(displayLine);
    const sourceLine = boundary.startLine + index;

    if (displayLine === "") {
      flushPending();
      continue;
    }

    const subparagraphMatch = SUBPARA_HEADING.exec(displayLine);
    if (subparagraphMatch?.[1] !== undefined) {
      flushPending();
      const displayHeading = normalizeHeading(
        stripMarkdownStrongEmphasis(subparagraphMatch[1]),
      );
      const identity = subparagraphIdentity(
        currentParagraph.id,
        displayHeading,
      );
      currentSubparagraph = {
        id: identity.id,
        kind: "SUBPARA",
        displayHeading,
        spokenHeading: identity.spoken,
        sourceLine,
        items: [],
      };
      currentParagraph.subparagraphs.push(currentSubparagraph);
      if (!identity.recognized)
        warnings.push(warningForHeading(displayHeading, sourceLine));
      continue;
    }

    const paragraphMatch = PARA_HEADING.exec(displayLine);
    if (paragraphMatch?.[1] !== undefined) {
      flushPending();
      const displayHeading = normalizeHeading(
        stripMarkdownStrongEmphasis(paragraphMatch[1]),
      );
      const identity = paragraphIdentity(displayHeading);
      currentParagraph = {
        id: identity.id,
        kind: "PARA",
        displayHeading,
        spokenHeading: identity.spoken,
        sourceLine,
        items: [],
        subparagraphs: [],
      };
      currentSubparagraph = undefined;
      paragraphs.push(currentParagraph);
      if (!identity.recognized)
        warnings.push(warningForHeading(displayHeading, sourceLine));
      continue;
    }

    const cells = tableCells(displayLine);
    if (cells !== null) {
      flushPending();
      if (!isTableDelimiter(cells)) {
        const displayCells = cells.map(stripMarkdownStrongEmphasis);
        const displayText = displayCells.join(" | ");
        const spokenText = displayCells
          .filter((cell) => cell !== "")
          .map(toSpokenText)
          .join(". ");
        pending = {
          displayText,
          spokenText,
          sourceLine,
          type: "table",
        };
        flushPending();
      }
      continue;
    }

    const labelMatch = LABEL_ITEM.exec(displayLine);
    if (labelMatch?.[1] !== undefined && labelMatch[2] !== undefined) {
      flushPending();
      const label = normalizeHeading(labelMatch[1]);
      const value = stripMarkdownStrongEmphasis(labelMatch[2].trim());
      const spokenLabel = toSpokenText(label);
      const spokenValue = toSpokenText(value);
      pending = {
        label,
        displayText: itemText(label, value),
        spokenText: itemText(spokenLabel, spokenValue),
        sourceLine,
        type: "label",
      };
      continue;
    }

    const numberedMatch = NUMBERED_ITEM.exec(displayLine);
    if (numberedMatch?.[1] !== undefined) {
      flushPending();
      const displayValue = stripMarkdownStrongEmphasis(numberedMatch[1]);
      const spokenValue = toSpokenText(numberedMatch[1]);
      pending = {
        displayText: displayValue,
        spokenText: spokenValue,
        sourceLine,
        type: "numbered",
      };
      continue;
    }

    const bulletMatch = BULLET_ITEM.exec(displayLine);
    if (bulletMatch?.[1] !== undefined) {
      flushPending();
      const displayValue = stripMarkdownStrongEmphasis(bulletMatch[1]);
      const spokenValue = toSpokenText(bulletMatch[1]);
      pending = {
        displayText: displayValue,
        spokenText: spokenValue,
        sourceLine,
        type: "bullet",
      };
      continue;
    }

    if (pending === undefined) {
      pending = {
        displayText: plainDisplayLine,
        spokenText: spokenLine,
        sourceLine,
        type: "paragraph",
      };
    } else {
      appendContinuation(pending, plainDisplayLine, spokenLine);
    }
  }

  flushPending();
  const allItems = paragraphs.flatMap((paragraph) => [
    ...paragraph.items,
    ...paragraph.subparagraphs.flatMap((subparagraph) => subparagraph.items),
  ]);
  const cues: ParsedCue[] = cleaned.cues.map((cue) => {
    const absoluteLine = boundary.startLine + cue.sourceLine - 1;
    const nextItem = allItems.find((item) => item.sourceLine > absoluteLine);
    const base = { name: cue.name, sourceLine: absoluteLine };
    return nextItem === undefined
      ? base
      : { ...base, beforeItemId: nextItem.id };
  });
  const immutableParagraphs: ParagraphNode[] = paragraphs.map((paragraph) => ({
    ...paragraph,
    subparagraphs: paragraph.subparagraphs.map(
      (subparagraph): SubparagraphNode => ({ ...subparagraph }),
    ),
  }));
  const spokenTextStream = immutableParagraphs.flatMap((paragraph) => [
    paragraph.spokenHeading,
    ...paragraph.items.flatMap((item) =>
      item.chunks.map((chunk) => chunk.spokenText),
    ),
    ...paragraph.subparagraphs.flatMap((subparagraph) => [
      subparagraph.spokenHeading,
      ...subparagraph.items.flatMap((item) =>
        item.chunks.map((chunk) => chunk.spokenText),
      ),
    ]),
  ]);
  const tree: OrdersParseTree = {
    schemaVersion: 1,
    title: boundary.title,
    paragraphs: immutableParagraphs,
    warnings,
    cues,
    spokenTextStream,
  };

  return { ok: true, tree };
}

export function parseOrdersMarkdown(source: string): ParseResult {
  return parseMarkdown(source, extractDeliverySource);
}

export function parseWarningOrderMarkdown(source: string): ParseResult {
  return parseMarkdown(source, extractWarningOrderDeliverySource);
}

export function parseTrainingMarkdown(source: string): ParseResult {
  return isWarningOrderSource(source)
    ? parseWarningOrderMarkdown(source)
    : parseOrdersMarkdown(source);
}
